import React, { useEffect, useRef, useState } from 'react';
import {
  Page,
  Popup,
  Navbar,
  Block,
  Row,
  Col,
  BlockTitle,
  Button,
  NavRight,
  Link,
  f7
} from 'framework7-react';
import II18 from '../../../translate/II18';
import { gae } from '../../../helpers/Analytics';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import PaperflyAPI from '../../../../server/api/PaperflyAPI';
import {
  EXTERNAL_POPUP_WINDOW_STATUS,
  prepareExternalPopUpWindow,
  SIGNME_REGISTRATION_STATUS,
  setExternalPopUpWindowStatus,
  setRegistrationData
} from '../../../../redux/actions/rootAction';
import {
  receiveESignatureData,
  receiveSignmeRegistrationStatus,
  requestDocumentESignature,
  resetDocumentESignatureStatus,
  resetSignmeRegistrationStatus,
  setSignmeIdentityId
} from '../../../../redux/actions/esignature';

import { SignmeHtmlForm, openSignMePopupWin } from '../utils';
import {
  initWebSocket,
  subscribeWebSocket,
  unSubscribeWebSocket
} from '../../../../redux/actions/websocket';
import { WEBSOCKET_TOPICS } from '../../../../redux/middleware/webSocket';
import { startCronJob, stopCronJob } from '../../../../redux/actions/cronJobs';
import RegistrationForm from '../RegistrationForm';
import { signmeRegisterFormStructure } from './signmeRegisterFormStructure';
import { findAllFormErrors } from '../../../helpers/formUtils';
import { confirmDialog } from '../../../helpers/dialogUtils';
import { useRegistrationData } from '../useRegistrationData';
import { checkSignmeAccount } from './signmeUtils';
import QesStepList, { QES_STEP_STATUS } from '../QesStepList';
import QesSuccessFooter from '../QesSuccessFooter';
import QesCooldownFillButton from '../QesCooldownFillButton';
import { QES_STEP_ID, signmeProvider } from '../providers';
import { createSignatureStatusWatch } from '../signatureStatusWatch';
import {
  isUserCredentialsProcessing,
  isUserCredentialsEmbedInCaller
} from '../../../../redux/userCredentialsTask';
import { isCreateTemplatePreparing } from '../../../../redux/createTemplateRequestTask';
import UserCredentialsStep from '../UserCredentialsStep';
import InfoTooltipIcon from '../../../common/InfoTooltipIcon';

const emptyStepMap = () => ({
  [QES_STEP_ID.CREATE_REQUEST]: null,
  [QES_STEP_ID.PREPARE_DOCUMENT]: null,
  [QES_STEP_ID.CHECK_REGISTRATION]: null,
  [QES_STEP_ID.COMPLETE_REGISTRATION]: null,
  [QES_STEP_ID.COMPLETE_SIGNING]: null,
  [QES_STEP_ID.SIGNED_SUCCESS]: null
});

export default function SignmeRegisterPopUp(props) {
  const {
    opened,
    onClose,
    noanimate,
    docId,
    signatureFieldId,
    documentIsReadyToSign,
    prepareDocumentToSign,
    prepareDocumentToSignError,
    currentRecipientData,
    setWaitingForProcessing,
    templateMode,
    signmeIdentityId,
    documentIsQualifiedSigned,
    onSignFlowDone,
    onRegistrationCancelled
  } = props;

  const { registrationData } = useRegistrationData(
    signatureFieldId,
    signmeRegisterFormStructure,
    currentRecipientData
  );
  const registrationStatus = useSelector(state => state.registrationStatus);
  const recipientRequestId = useSelector(state => state.recipientRequestId);
  const externalPopUpStatus = useSelector(state => state.externalPopUpStatus);
  const externalPopUpOptions = useSelector(state => state.externalPopUpOptions);
  const userCredentialsTask = useSelector(state => state.userCredentialsTask);
  const createTemplateRequestTask = useSelector(
    state => state.createTemplateRequestTask
  );
  const credentialsActive = isUserCredentialsProcessing(userCredentialsTask);
  const embedCredentials =
    credentialsActive && isUserCredentialsEmbedInCaller(userCredentialsTask);
  const credsStepRef = useRef(null);
  const [credsValid, setCredsValid] = useState(false);

  const dispatch = useDispatch();
  const extStatusRef = useRef(externalPopUpStatus);
  extStatusRef.current = externalPopUpStatus;
  const extOptionsRef = useRef(externalPopUpOptions);
  extOptionsRef.current = externalPopUpOptions;
  const signRequestRef = useRef(null);
  const signWatchRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [signDocumentRequest, setSignDocumentRequest] = useState(null);

  const [registrationRequest, setRegistrationRequest] = useState(null);
  const [
    waitForDocumentIsReadyToSign,
    setWaitForDocumentIsReadyToSign
  ] = useState(false);
  const [lastError, setLastError] = useState('');
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [stepStatus, setStepStatus] = useState(emptyStepMap);
  const [readyForCta, setReadyForCta] = useState(false);
  const [startingCta, setStartingCta] = useState(false);
  const [signatureStatusChecking, setSignatureStatusChecking] = useState(false);
  const [restartCooldown, setRestartCooldown] = useState(false);
  const [phase, setPhase] = useState('form'); // form | preparing | success
  const signOnly = Boolean(signmeIdentityId);
  const prevExtPopUpStatusRef = useRef(externalPopUpStatus);
  const prevTemplateModeRef = useRef(templateMode);
  const signSuccessHandledRef = useRef(false);
  // Intentional close after registration — do not treat as user-abort restore
  const skipRestoreOnCloseRef = useRef(false);
  const stepStatusRef = useRef(stepStatus);
  stepStatusRef.current = stepStatus;
  const signatureStatusCheckingRef = useRef(signatureStatusChecking);
  signatureStatusCheckingRef.current = signatureStatusChecking;

  const setStep = (id, status) => {
    setStepStatus(prev => ({ ...prev, [id]: status }));
  };

  const resetFlow = () => {
    signWatchRef.current?.pause();
    signRequestRef.current = null;
    setSignDocumentRequest(null);
    setShowSteps(false);
    setStepStatus(emptyStepMap());
    setReadyForCta(false);
    setStartingCta(false);
    setSignatureStatusChecking(false);
    setPhase(signOnly ? 'preparing' : 'form');
    signSuccessHandledRef.current = false;
  };

  const isSignaturePollActive = () =>
    signatureStatusCheckingRef.current ||
    stepStatusRef.current[QES_STEP_ID.COMPLETE_SIGNING] ===
      QES_STEP_STATUS.RUNNING;

  const confirmAbortSignatureStatusCheck = onConfirm => {
    confirmDialog(
      II18.get('title_confirmation'),
      II18.get('dialog_signme_confirm_abort_status_check'),
      onConfirm,
      undefined,
      II18.get('title_yes'),
      II18.get('title_no')
    );
  };

  /** Provider window closed while signing — keep WS/poll; show status-check UI. */
  const enterSignatureStatusCheck = () => {
    gae('SIGNME_REGISTER_POP_UP', 'EXT_POPUP_CLOSED_STATUS_CHECK');
    setSignatureStatusChecking(true);
    setStartingCta(false);
    setReadyForCta(true);
    setPhase('preparing');
    setShowSteps(true);
    // Cron may still be waiting on the 30s delay — start polling now.
    signWatchRef.current?.ensurePolling?.();
  };

  // Restart CTA: 3s cooldown so poll/WS can settle before the user can abort+retry.
  useEffect(() => {
    if (!signatureStatusChecking) {
      setRestartCooldown(false);
      return undefined;
    }
    setRestartCooldown(true);
    const t = setTimeout(() => setRestartCooldown(false), 5000);
    return () => clearTimeout(t);
  }, [signatureStatusChecking]);

  const preparePopUpWindow = () => {
    if (externalPopUpStatus !== EXTERNAL_POPUP_WINDOW_STATUS.OPEN) {
      gae('SIGNME_REGISTER_POP_UP', 'PREPARE_EXT_POPUP');
      const winInfo = openSignMePopupWin();
      dispatch(prepareExternalPopUpWindow(winInfo));
      return winInfo;
    }
    return {
      winReference: extOptionsRef.current?.winReference
    };
  };

  const isPopupBlocked = winInfo => {
    const win = winInfo?.winReference;
    return !win || win.closed || typeof win.closed === 'undefined';
  };

  /** Close registration IdP window so spinner clears and Sign Start CTA is clickable. */
  const closeRegistrationPopupForSignCta = () => {
    skipRestoreOnCloseRef.current = true;
    const win = extOptionsRef.current?.winReference;
    if (win && !win.closed) {
      try {
        win.close();
      } catch (e) {
        // ignore
      }
    }
    gae('SIGNME_REGISTER_POP_UP', 'CLOSE_EXT_POPUP_AFTER_REG');
    dispatch(setExternalPopUpWindowStatus(EXTERNAL_POPUP_WINDOW_STATUS.CLOSE));
  };

  const fatalSignError = err => {
    gae('ESIGN_PROCESS', 'ERROR', err);
    signWatchRef.current?.pause();
    dispatch(setExternalPopUpWindowStatus(EXTERNAL_POPUP_WINDOW_STATUS.CLOSE));
    f7.dialog.alert(
      II18.get('esignature_sign_document_error_message', err),
      II18.get('esignature_sign_document_error_title')
    );
    onCloseRef.current?.();
  };

  const submitSignRequest = req => {
    gae('ESIGN_PROCESS', 'SUBMIT_DATA_TO_EXT_POPUP');
    dispatch(requestDocumentESignature(docId, req.internalCallbackId));
    // Form must be in DOM — setState before submit; short delay for paint
    setTimeout(() => {
      signmeProvider.submitToPopup();
      signWatchRef.current?.start(req.internalCallbackId, signmeProvider);
    }, 300);
  };

  const createSignmeSignProcess = identityId => {
    gae('SIGNME_SIGN_PROCESS', 'CREATE_SIGNATURE_PROCESS');
    dispatch(resetDocumentESignatureStatus(docId));
    PaperflyAPI.signmeCreateSignatureProcess(
      recipientRequestId,
      [docId],
      identityId
    ).then(
      response => {
        if (response.error) {
          fatalSignError(response.error);
          return;
        }
        gae('SIGNME_SIGN_PROCESS', 'RECEIVE_PROCESS_DATA');
        const req = {
          signme: true,
          url: response.signmeSignatureProcess.url,
          skey: response.signmeSignatureProcess.signatureProcessId,
          iid: response.signmeSignatureProcess.identityId,
          internalCallbackId: response.signmeSignatureProcess.internalCallbackId
        };
        signRequestRef.current = req;
        setSignDocumentRequest(req);
        submitSignRequest(req);
      },
      error => {
        fatalSignError(
          error.response?.data?.error || error.response?.data?.errorCode
        );
      }
    );
  };

  const beginSignmeSigning = () => {
    // Must open under the click gesture; check immediately (PREPARE→CLOSE may
    // batch away before React sees PREPARE — same pattern as A-Trust).
    const winInfo = preparePopUpWindow();
    if (isPopupBlocked(winInfo)) {
      markPopupBlocked(QES_STEP_ID.COMPLETE_SIGNING);
      return;
    }
    const existing = signRequestRef.current;
    if (existing) {
      submitSignRequest(existing);
      return;
    }
    const iid = signmeIdentityId;
    if (!iid) {
      fatalSignError('missing_signme_identity');
      return;
    }
    createSignmeSignProcess(iid);
  };

  const getSignmeRegistrationData = () => ({
    familyNames: registrationData.get('lastName'),
    givenNames: registrationData.get('firstName'),
    mobilePhoneNumber: (sn => `${sn?.code} ${sn.number}`)(
      registrationData.get('phone')
    ),
    emailAddress: registrationData.get('email')
  });

  const errorOnRegistration = error => {
    gae('SIGNME_REGISTER_POP_UP', 'ERROR', error);
    setLastError(error);
    dispatch(setExternalPopUpWindowStatus(EXTERNAL_POPUP_WINDOW_STATUS.CLOSE));
    setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.ERROR);
    setStartingCta(false);
  };

  const markPopupBlocked = stepId => {
    gae('SIGNME_REGISTER_POP_UP', 'POPUP_BLOCKED', stepId);
    // Drop any in-flight create so Register/Start retry can open a fresh process.
    setRegistrationRequest(null);
    if (stepId === QES_STEP_ID.COMPLETE_SIGNING) {
      setSignatureStatusChecking(false);
    }
    setStep(stepId, QES_STEP_STATUS.BLOCKED);
    setStartingCta(false);
    setReadyForCta(true);
  };

  /** User closed provider popup — back to hourglass + CTA, not blocked. */
  const restoreAwaitingUser = stepId => {
    gae('SIGNME_REGISTER_POP_UP', 'EXT_POPUP_CLOSED_RESTORE_AWAIT', stepId);
    if (stepId === QES_STEP_ID.COMPLETE_SIGNING) {
      signWatchRef.current?.pause();
      setSignatureStatusChecking(false);
    } else if (stepId === QES_STEP_ID.COMPLETE_REGISTRATION) {
      stopCheckingRegistration();
    }
    setStep(stepId, QES_STEP_STATUS.AWAITING_USER);
    setStartingCta(false);
    setReadyForCta(true);
    setPhase('preparing');
    setShowSteps(true);
  };

  const beginSignOnlyPrep = () => {
    setShowSteps(true);
    setPhase('preparing');
    const next = emptyStepMap();
    if (templateMode) {
      next[QES_STEP_ID.CREATE_REQUEST] = QES_STEP_STATUS.RUNNING;
    } else if (!documentIsReadyToSign) {
      next[QES_STEP_ID.PREPARE_DOCUMENT] = QES_STEP_STATUS.RUNNING;
    }
    if (documentIsReadyToSign) {
      next[QES_STEP_ID.COMPLETE_SIGNING] = QES_STEP_STATUS.AWAITING_USER;
      setStepStatus(next);
      setReadyForCta(true);
      return;
    }
    setStepStatus(next);

    setWaitForDocumentIsReadyToSign(true);
    prepareDocumentToSign();
  };

  const finishPrepShowSignCta = () => {
    if (stepStatusRef.current[QES_STEP_ID.CREATE_REQUEST] != null) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.DONE);
    }
    if (stepStatusRef.current[QES_STEP_ID.PREPARE_DOCUMENT] != null) {
      setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.DONE);
    }
    setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.AWAITING_USER);
    setReadyForCta(true);
    setStartingCta(false);
    setShowSteps(true);
    setPhase('preparing');
  };

  const beginRegistrationWithSteps = () => {
    const next = emptyStepMap();
    if (templateMode) {
      next[QES_STEP_ID.CREATE_REQUEST] = QES_STEP_STATUS.RUNNING;
    } else if (!documentIsReadyToSign) {
      next[QES_STEP_ID.PREPARE_DOCUMENT] = QES_STEP_STATUS.RUNNING;
    }
    setStepStatus(next);
    setShowSteps(true);
    setPhase('preparing');
  };

  const applyChanges = () => {
    const errors = findAllFormErrors(
      [signmeRegisterFormStructure],
      registrationData
    );

    if (errors.size > 0) {
      gae('SIGNME_REGISTER_POP_UP', 'CANT_START_REGISTRATION.FORM_ERRORS');
      setShowFormErrors(true);
      setWaitingForProcessing?.(false);
      return;
    }

    gae('SIGNME_REGISTER_POP_UP', 'START_REGISTRATION');
    beginRegistrationWithSteps();

    if (!documentIsReadyToSign) {
      setWaitForDocumentIsReadyToSign(true);
      prepareDocumentToSign();
      return;
    }

    continueRegistrationAfterPrep();
  };

  const continueRegistrationAfterPrep = () => {
    finishPrepThenProbeAccount();
  };

  /** After doc prep: probe account with form data; skip Register CTA if already signable. */
  const finishPrepThenProbeAccount = () => {
    if (stepStatusRef.current[QES_STEP_ID.CREATE_REQUEST] != null) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.DONE);
    }
    if (stepStatusRef.current[QES_STEP_ID.PREPARE_DOCUMENT] != null) {
      setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.DONE);
    }
    setStep(QES_STEP_ID.CHECK_REGISTRATION, QES_STEP_STATUS.RUNNING);
    setReadyForCta(false);
    setStartingCta(false);

    const data = getSignmeRegistrationData();
    checkSignmeAccount(
      (result, userData) => {
        if (result) {
          gae('SIGNME_REGISTER_POP_UP', 'USER_ALREADY_EXISTS');
          // Show check_registration only when an account already exists and
          // signing can start immediately.
          setStep(QES_STEP_ID.CHECK_REGISTRATION, QES_STEP_STATUS.DONE);
          dispatch(setSignmeIdentityId(userData.identityId));
          setStep(QES_STEP_ID.COMPLETE_REGISTRATION, null);
          setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.AWAITING_USER);
          setReadyForCta(true);
          setStartingCta(false);
          return;
        }
        // Need registration — keep check_registration out of the step list.
        setStep(QES_STEP_ID.CHECK_REGISTRATION, null);
        setStep(
          QES_STEP_ID.COMPLETE_REGISTRATION,
          QES_STEP_STATUS.AWAITING_USER
        );
        setReadyForCta(true);
        setStartingCta(false);
      },
      recipientRequestId,
      data.emailAddress,
      data.mobilePhoneNumber,
      data.familyNames,
      data.givenNames
    );
  };

  const createRegistrationProcess = data => {
    if (registrationRequest) {
      return;
    }

    setLastError('');
    gae('SIGNME_REGISTER_POP_UP', 'CREATE_REGISTRATION_PROCESS');

    PaperflyAPI.signmeCreateRegistrationProcess(
      data.mobilePhoneNumber,
      data.emailAddress,
      data.givenNames,
      data.familyNames,
      recipientRequestId,
      null,
      window.isDesktop
    ).then(
      response => {
        if (response.error) {
          errorOnRegistration(response.error);
        } else {
          gae('SIGNME_REGISTER_POP_UP', 'SET_REGISTRATION_REQUEST');
          setRegistrationRequest({
            url: response.signmeRegistrationProcess.url,
            VKEY:
              response.signmeRegistrationProcess.identityVerificationProcessId,
            iid: response.signmeRegistrationProcess.identityId,
            internalCallbackId:
              response.signmeRegistrationProcess.internalCallbackId
          });
        }
      },
      error => {
        errorOnRegistration(
          error.response.data.error || error.response.data.errorCode
        );
      }
    );
  };

  const runRegistrationUnderGesture = () => {
    setStartingCta(true);
    setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.RUNNING);

    // Open under click gesture — mobile blocks window.open after async work.
    const winInfo = preparePopUpWindow();
    if (isPopupBlocked(winInfo)) {
      markPopupBlocked(QES_STEP_ID.COMPLETE_REGISTRATION);
      return;
    }

    // Account branch already decided in finishPrepThenProbeAccount (Register CTA).
    createRegistrationProcess(getSignmeRegistrationData());
  };

  const runSignUnderGesture = () => {
    setSignatureStatusChecking(false);
    setStartingCta(true);
    setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.RUNNING);
    beginSignmeSigning();
  };

  const onCtaClick = () => {
    if (signatureStatusCheckingRef.current) {
      confirmDialog(
        II18.get('title_confirmation'),
        II18.get('dialog_signme_confirm_restart_signing'),
        () => {
          gae('SIGNME_REGISTER_POP_UP', 'STATUS_CHECK_RESTART_CONFIRMED');
          signWatchRef.current?.pause();
          // Closed IdP session may invalidate the prior process — fresh create.
          signRequestRef.current = null;
          setSignDocumentRequest(null);
          runSignUnderGesture();
        },
        undefined,
        II18.get('title_yes'),
        II18.get('title_no')
      );
      return;
    }
    const ss = stepStatusRef.current;
    const regAwait =
      ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.AWAITING_USER ||
      ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.BLOCKED;
    if (regAwait) {
      runRegistrationUnderGesture();
      return;
    }
    runSignUnderGesture();
  };

  const stopCheckingRegistration = () => {
    setWaitingForProcessing?.(false);
    if (registrationRequest?.internalCallbackId) {
      dispatch(stopCronJob(registrationRequest.internalCallbackId));
    }
    dispatch(unSubscribeWebSocket(WEBSOCKET_TOPICS.SIGNME_REGISTRATION));
  };

  const startCheckingRegistration = () => {
    gae('SIGNME_REGISTER_POP_UP', 'START_REGISTRATION_CRONJOB');

    dispatch(
      subscribeWebSocket(
        WEBSOCKET_TOPICS.SIGNME_REGISTRATION,
        registrationRequest.internalCallbackId
      )
    );

    dispatch(
      startCronJob(registrationRequest.internalCallbackId, 1000, function() {
        checkSignmeAccount((accountReady, userData) => {
          if (accountReady && userData?.registrationDialogClosed) {
            dispatch(
              receiveSignmeRegistrationStatus(SIGNME_REGISTRATION_STATUS.OK)
            );
            stopCheckingRegistration();
          }
        }, recipientRequestId);
      })
    );
  };

  useEffect(() => {
    signWatchRef.current = createSignatureStatusWatch({
      startCronJob: (id, period, doJob) =>
        dispatch(startCronJob(id, period, doJob)),
      stopCronJob: id => dispatch(stopCronJob(id)),
      subscribeWebSocket: (topic, id) =>
        dispatch(subscribeWebSocket(topic, id)),
      unSubscribeWebSocket: topic => dispatch(unSubscribeWebSocket(topic)),
      getExternalPopUpStatus: () => extStatusRef.current,
      receiveESignatureData: data => dispatch(receiveESignatureData(data)),
      onProcessFailed: () => fatalSignError(),
      usePreloaderDialog: false
    });
    return () => signWatchRef.current?.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // SignMe identity / registration prefill (was HOC prepareQualifiedSignature)
  useEffect(() => {
    if (templateMode || signmeIdentityId) return;
    gae('ESIGN_PROCESS', 'PREPARE_SIGNME');
    checkSignmeAccount((result, userData) => {
      if (result) {
        gae('SIGNME_SIGN_PROCESS', 'USER_ALREADY_EXISTS');
        gae('SIGNME_SIGN_PROCESS', 'SET_IDENTITY_ID');
        dispatch(setSignmeIdentityId(userData.identityId));
      } else if (userData) {
        gae('SIGNME_SIGN_PROCESS', 'USER_DATA_PREPARED');
        dispatch(
          setRegistrationData(
            new Map(
              Object.entries({
                firstName: userData.givenNames,
                lastName: userData.familyNames,
                phone: userData.mobilePhoneNumber,
                email: userData.emailAddress
              })
            )
          )
        );
      }
    }, recipientRequestId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (opened && signOnly) {
      // Identity set mid-register prep already advanced to Sign CTA — do not reset steps
      const ss = stepStatusRef.current;
      if (
        ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.AWAITING_USER ||
        ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.BLOCKED ||
        ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.RUNNING ||
        ss[QES_STEP_ID.CHECK_REGISTRATION] === QES_STEP_STATUS.DONE
      ) {
        return;
      }
      beginSignOnlyPrep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, signOnly]);

  useEffect(() => {
    if (!showSteps || !opened) return;
    if (embedCredentials) {
      return;
    }
    if (
      credentialsActive &&
      stepStatus[QES_STEP_ID.CREATE_REQUEST] === QES_STEP_STATUS.RUNNING
    ) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.AWAITING_USER);
    } else if (
      !credentialsActive &&
      stepStatus[QES_STEP_ID.CREATE_REQUEST] ===
        QES_STEP_STATUS.AWAITING_USER &&
      templateMode &&
      isCreateTemplatePreparing(createTemplateRequestTask)
    ) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.RUNNING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    credentialsActive,
    embedCredentials,
    showSteps,
    opened,
    createTemplateRequestTask,
    templateMode
  ]);

  useEffect(() => {
    if (!opened || !showSteps || !waitForDocumentIsReadyToSign) return;
    const wasTemplate = prevTemplateModeRef.current;
    prevTemplateModeRef.current = templateMode;
    if (wasTemplate && !templateMode) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.DONE);
      if (!documentIsReadyToSign) {
        setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.RUNNING);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateMode]);

  useEffect(() => {
    if (opened) {
      if (registrationRequest) {
        if (externalPopUpStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN) {
          startCheckingRegistration();
          dispatch(resetSignmeRegistrationStatus());
          gae('SIGNME_REGISTER_POP_UP', 'SUBMIT_REGISTRATION_DATA');
          document.getElementById('signmeRegister')?.submit();
        } else {
          stopCheckingRegistration();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPopUpStatus, registrationRequest]);

  useEffect(() => {
    const prevStatus = prevExtPopUpStatusRef.current;
    prevExtPopUpStatusRef.current = externalPopUpStatus;

    if (externalPopUpStatus !== EXTERNAL_POPUP_WINDOW_STATUS.CLOSE) {
      return;
    }

    // Intentional close after registration / already-exists — keep signing CTA
    if (skipRestoreOnCloseRef.current) {
      skipRestoreOnCloseRef.current = false;
      return;
    }

    // Backup when withExternalPopupWindow forces CLOSE on blocked open.
    // Primary detection is sync after window.open (see run*UnderGesture).
    if (prevStatus === EXTERNAL_POPUP_WINDOW_STATUS.PREPARE) {
      const ss = stepStatusRef.current;
      if (ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.RUNNING) {
        markPopupBlocked(QES_STEP_ID.COMPLETE_SIGNING);
      } else if (
        ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.RUNNING ||
        startingCta
      ) {
        markPopupBlocked(QES_STEP_ID.COMPLETE_REGISTRATION);
      }
      return;
    }

    if (prevStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN && opened) {
      const ss = stepStatusRef.current;
      if (ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.RUNNING) {
        // Keep WS/poll — sign-me may close before processState is OK.
        enterSignatureStatusCheck();
      } else if (
        ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.RUNNING
      ) {
        restoreAwaitingUser(QES_STEP_ID.COMPLETE_REGISTRATION);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPopUpStatus, startingCta, opened]);

  useEffect(() => {
    if (opened && documentIsReadyToSign && waitForDocumentIsReadyToSign) {
      setWaitForDocumentIsReadyToSign(false);
      if (signOnly) {
        finishPrepShowSignCta();
      } else {
        continueRegistrationAfterPrep();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentIsReadyToSign]);

  useEffect(() => {
    if (opened && prepareDocumentToSignError) {
      setWaitingForProcessing?.(false);
      setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareDocumentToSignError]);

  useEffect(() => {
    if (opened && registrationStatus) {
      const iid = registrationRequest?.iid;
      setRegistrationRequest(null);
      stopCheckingRegistration();

      if (registrationStatus === SIGNME_REGISTRATION_STATUS.OK) {
        gae('SIGNME_REGISTER_POP_UP', 'SET_IDENTITY_ID');
        if (iid) dispatch(setSignmeIdentityId(iid));
        // Drop registration window + spinner; Sign Start opens a fresh popup under gesture
        closeRegistrationPopupForSignCta();
        setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.DONE);
        setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.AWAITING_USER);
        setReadyForCta(true);
        setStartingCta(false);
      }
      if (registrationStatus === SIGNME_REGISTRATION_STATUS.CANCEL) {
        onRegistrationCancelled?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationStatus]);

  useEffect(() => {
    if (
      opened &&
      documentIsQualifiedSigned &&
      !signSuccessHandledRef.current &&
      showSteps
    ) {
      signSuccessHandledRef.current = true;
      signWatchRef.current?.pause();
      setSignatureStatusChecking(false);
      setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.DONE);
      setStep(QES_STEP_ID.SIGNED_SUCCESS, QES_STEP_STATUS.DONE);
      setPhase('success');
      setReadyForCta(false);
      setStartingCta(false);
    }
  }, [documentIsQualifiedSigned, opened, showSteps]);

  useEffect(() => {
    if (opened) {
      dispatch(initWebSocket());
      setWaitingForProcessing?.(false);
      setWaitForDocumentIsReadyToSign(false);
      setLastError('');
      setShowFormErrors(false);
      setRegistrationRequest(null);
      setSignatureStatusChecking(false);
      if (!signOnly) {
        resetFlow();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const providerName = signmeProvider.displayName;

  const buildVisibleSteps = () => {
    const order = [
      QES_STEP_ID.CREATE_REQUEST,
      QES_STEP_ID.PREPARE_DOCUMENT,
      QES_STEP_ID.CHECK_REGISTRATION,
      QES_STEP_ID.COMPLETE_REGISTRATION,
      QES_STEP_ID.COMPLETE_SIGNING,
      QES_STEP_ID.SIGNED_SUCCESS
    ];
    const labels = {
      [QES_STEP_ID.CREATE_REQUEST]: 'qes_step_create_request',
      [QES_STEP_ID.PREPARE_DOCUMENT]: 'qes_step_prepare_document',
      [QES_STEP_ID.CHECK_REGISTRATION]: 'qes_step_check_registration',
      [QES_STEP_ID.COMPLETE_REGISTRATION]: II18.get(
        'qes_step_complete_registration',
        providerName
      ),
      [QES_STEP_ID.COMPLETE_SIGNING]: signatureStatusChecking
        ? II18.get('qes_step_check_signature')
        : II18.get('qes_step_complete_signing', providerName),
      [QES_STEP_ID.SIGNED_SUCCESS]: 'esignature_document_signed'
    };
    const registrationJustCompleted =
      stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.DONE;

    const hints = {
      [QES_STEP_ID.CREATE_REQUEST]: 'qes_step_create_request_hint',
      [QES_STEP_ID.COMPLETE_REGISTRATION]: signmeProvider.nopopupMessageKey,
      [QES_STEP_ID.COMPLETE_SIGNING]: signatureStatusChecking
        ? 'dialog_signme_status_check_hint'
        : registrationJustCompleted
        ? 'dialog_signme_registration_done_sign_hint'
        : signmeProvider.nopopupMessageKey
    };

    const createRequestAction = showCreateRequestCta ? (
      <Button
        id="signme_retry_create_request"
        raised
        fill
        style={{ minWidth: 120 }}
        onClick={onRetryCreateRequest}
      >
        {II18.get('title_next')}
      </Button>
    ) : null;

    const ctaStepIds = new Set([
      QES_STEP_ID.COMPLETE_REGISTRATION,
      QES_STEP_ID.COMPLETE_SIGNING
    ]);

    const stepAction = showCta ? (
      signatureStatusChecking ? (
        <QesCooldownFillButton
          id="signme_start_cta"
          cooling={restartCooldown}
          disabled={startingCta}
          durationMs={5000}
          style={{ minWidth: 120 }}
          onClick={onCtaClick}
        >
          {II18.get('dialog_signme_restart_signing')}
        </QesCooldownFillButton>
      ) : (
        <Button
          id="signme_start_cta"
          raised
          fill
          style={{ minWidth: 120 }}
          disabled={startingCta}
          onClick={onCtaClick}
        >
          {ctaIsRegister
            ? II18.get('signme_settings_label_register')
            : II18.get('title_start')}
        </Button>
      )
    ) : null;

    const prepCollapseIds = new Set([
      QES_STEP_ID.CREATE_REQUEST,
      QES_STEP_ID.PREPARE_DOCUMENT,
      QES_STEP_ID.CHECK_REGISTRATION,
      QES_STEP_ID.COMPLETE_REGISTRATION
    ]);

    const steps = order
      .filter(id => {
        const status = stepStatus[id];
        if (status == null || status === QES_STEP_STATUS.PENDING) return false;
        // Probe runs silently; only surface the step when an account was found.
        if (
          id === QES_STEP_ID.CHECK_REGISTRATION &&
          status !== QES_STEP_STATUS.DONE
        ) {
          return false;
        }
        if (registrationJustCompleted && prepCollapseIds.has(id)) {
          return false;
        }
        return true;
      })
      .map(id => ({
        id,
        label: labels[id],
        status: stepStatus[id],
        hint: hints[id],
        hintHelp:
          ctaStepIds.has(id) &&
          !signatureStatusChecking &&
          !(id === QES_STEP_ID.COMPLETE_SIGNING && registrationJustCompleted),
        framed:
          registrationJustCompleted &&
          id === QES_STEP_ID.COMPLETE_SIGNING &&
          stepStatus[id] !== QES_STEP_STATUS.DONE,
        action:
          id === QES_STEP_ID.CREATE_REQUEST
            ? createRequestAction
            : ctaStepIds.has(id)
            ? stepAction
            : undefined
      }));

    if (!registrationJustCompleted) {
      return steps;
    }

    return [
      {
        id: 'prep_summary',
        label: 'qes_step_prep_completed',
        status: QES_STEP_STATUS.DONE,
        hint: 'qes_step_prep_completed_sub',
        hintWhenDone: true
      },
      ...steps
    ];
  };

  const showCta =
    phase === 'preparing' &&
    (signatureStatusChecking ||
      (readyForCta &&
        (stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
          QES_STEP_STATUS.AWAITING_USER ||
          stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
            QES_STEP_STATUS.BLOCKED ||
          stepStatus[QES_STEP_ID.COMPLETE_SIGNING] ===
            QES_STEP_STATUS.AWAITING_USER ||
          stepStatus[QES_STEP_ID.COMPLETE_SIGNING] ===
            QES_STEP_STATUS.BLOCKED)));

  // After credentials cancel — Weiter to reopen credentials / retry create
  const showCreateRequestCta =
    phase === 'preparing' &&
    !credentialsActive &&
    !embedCredentials &&
    stepStatus[QES_STEP_ID.CREATE_REQUEST] === QES_STEP_STATUS.AWAITING_USER;

  const onRetryCreateRequest = () => {
    gae('SIGNME_REGISTER_POP_UP', 'RETRY_CREATE_REQUEST');
    setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.RUNNING);
    setWaitForDocumentIsReadyToSign(true);
    prepareDocumentToSign();
  };

  const doCancelPreparing = () => {
    gae('SIGNME_REGISTER_POP_UP', 'USER_BACK_FROM_PREP');
    stopCheckingRegistration();
    signWatchRef.current?.pause();
    setSignatureStatusChecking(false);
    setShowSteps(false);
    setStepStatus(emptyStepMap());
    setWaitForDocumentIsReadyToSign(false);
    setReadyForCta(false);
    setStartingCta(false);
    setPhase(signOnly ? 'preparing' : 'form');
    if (signOnly) {
      onClose();
    }
  };

  const cancelPreparing = () => {
    if (isSignaturePollActive()) {
      confirmAbortSignatureStatusCheck(doCancelPreparing);
      return;
    }
    doCancelPreparing();
  };

  const onCredentialsBack = () => {
    gae('SIGNME_REGISTER_POP_UP', 'CREDENTIALS_BACK');
    credsStepRef.current?.cancel();
    cancelPreparing();
  };

  const onCredentialsNext = () => {
    gae('SIGNME_REGISTER_POP_UP', 'CREDENTIALS_NEXT');
    credsStepRef.current?.submit();
  };

  const prepBusy =
    startingCta ||
    embedCredentials ||
    stepStatus[QES_STEP_ID.CREATE_REQUEST] === QES_STEP_STATUS.RUNNING ||
    stepStatus[QES_STEP_ID.PREPARE_DOCUMENT] === QES_STEP_STATUS.RUNNING ||
    stepStatus[QES_STEP_ID.CHECK_REGISTRATION] === QES_STEP_STATUS.RUNNING;

  const ctaIsRegister =
    stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
      QES_STEP_STATUS.AWAITING_USER ||
    stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.BLOCKED;

  const preparingHelpKey = () => {
    if (embedCredentials) {
      return 'user_credentials_title';
    }
    if (showCreateRequestCta) {
      return 'qes_ready_create_request_help';
    }
    if (signatureStatusChecking) {
      return 'dialog_signme_status_check_help';
    }
    if (showCta) {
      return ctaIsRegister
        ? 'dialog_signme_ready_to_register_help'
        : 'dialog_signme_ready_to_sign_help';
    }
    if (
      stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.RUNNING
    ) {
      return 'dialog_signme_awaiting_register_help';
    }
    if (stepStatus[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.RUNNING) {
      return 'dialog_signme_awaiting_sign_help';
    }
    return signOnly
      ? 'dialog_signme_preparing_help_login'
      : 'dialog_signme_preparing_help';
  };

  const doHandleClose = () => {
    signWatchRef.current?.pause();
    stopCheckingRegistration();
    setSignatureStatusChecking(false);
    onClose();
  };

  const handleClose = () => {
    if (isSignaturePollActive()) {
      confirmAbortSignatureStatusCheck(doHandleClose);
      return;
    }
    doHandleClose();
  };

  return (
    <Popup
      id={'signme_register_popup'}
      className="signme_register_popup"
      animate={!noanimate}
      opened={Boolean(opened)}
      swipeToClose={false}
      closeByBackdropClick={false}
      closeByOutsideClick={false}
      onPopupClosed={() => {
        gae('SIGNME_REGISTER_POP_UP', 'CLOSE');
      }}
      onPopupOpen={() => gae('SIGNME_REGISTER_POP_UP', 'SHOW')}
    >
      <Page style={{ background: '#fff' }}>
        <Navbar title={II18.get('dialog_signme_settings_title')}>
          <NavRight>
            <Link onClick={handleClose}>
              <II18>title_close</II18>
            </Link>
          </NavRight>
        </Navbar>
        {opened && (
          <>
            {showSteps || phase === 'success' ? (
              <div
                id="signme_preparing"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '40px 24px 24px',
                  height: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                  {phase === 'success' ? (
                    <II18>esignature_document_signed</II18>
                  ) : embedCredentials ? (
                    <II18>user_credentials_title</II18>
                  ) : (
                    <II18>dialog_signme_preparing_title</II18>
                  )}
                </h3>
                {phase !== 'success' && !embedCredentials && (
                  <p
                    style={{
                      color: '#999',
                      fontSize: 14,
                      textAlign: 'center',
                      marginBottom: 24
                    }}
                  >
                    <II18>{preparingHelpKey()}</II18>
                  </p>
                )}
                {embedCredentials ? (
                  <UserCredentialsStep
                    ref={credsStepRef}
                    active={embedCredentials}
                    onValidityChange={setCredsValid}
                  />
                ) : (
                  <QesStepList steps={buildVisibleSteps()} />
                )}
                {lastError && (
                  <div
                    className="alert alert-danger"
                    role="alert"
                    style={{ marginTop: 16, width: '100%', maxWidth: 320 }}
                  >
                    {lastError}
                  </div>
                )}
                {phase === 'success' ? (
                  <QesSuccessFooter
                    onDone={() => {
                      if (onSignFlowDone) onSignFlowDone();
                      else onClose();
                    }}
                  />
                ) : embedCredentials ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-around',
                      width: '100%',
                      marginTop: 'auto',
                      padding: 30,
                      paddingTop: 10
                    }}
                  >
                    <Button
                      id="signme_credentials_back"
                      onClick={onCredentialsBack}
                    >
                      {II18.get(signOnly ? 'title_cancel' : 'title_back')}
                    </Button>
                    <Button
                      id="signme_credentials_next"
                      raised
                      fill
                      style={{ minWidth: 120 }}
                      disabled={!credsValid}
                      onClick={onCredentialsNext}
                    >
                      {userCredentialsTask?.customTitle ||
                        II18.get('title_next')}
                    </Button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      marginTop: 'auto',
                      padding: 30,
                      paddingTop: 10
                    }}
                  >
                    <Button
                      id="signme_prep_back"
                      onClick={cancelPreparing}
                      disabled={prepBusy}
                    >
                      {II18.get(signOnly ? 'title_cancel' : 'title_back')}
                    </Button>
                  </div>
                )}
                {registrationRequest &&
                  SignmeHtmlForm(
                    'signmeRegister',
                    registrationRequest.url,
                    registrationRequest.iid,
                    registrationRequest.VKEY
                  )}
                {signDocumentRequest?.signme &&
                  SignmeHtmlForm(
                    'signmeSignDocument',
                    signDocumentRequest.url,
                    signDocumentRequest.iid,
                    '',
                    signDocumentRequest.skey
                  )}
              </div>
            ) : (
              <Block style={{ minHeight: 170 }}>
                <>
                  <Block>
                    <II18>signme_settings_registration_info</II18>
                  </Block>

                  <BlockTitle>
                    <II18>signme_settings_registration_form_title</II18>
                    <InfoTooltipIcon
                      id="signme_registration_form_title_info"
                      text={II18.get('qes_registration_readonly_fields_hint')}
                    />
                  </BlockTitle>

                  {lastError && (
                    <Block>
                      <div className="alert alert-danger" role="alert">
                        {lastError}
                      </div>
                    </Block>
                  )}
                  {registrationStatus === SIGNME_REGISTRATION_STATUS.FAILED && (
                    <Block>
                      <div className="alert alert-danger" role="alert">
                        <II18>signme_settings_registration_failed</II18>
                      </div>
                    </Block>
                  )}

                  <div
                    style={{
                      flexGrow: 1,
                      overflow: 'hidden',
                      overflowY: 'auto',
                      padding: 10,
                      height: '100%'
                    }}
                    className={'tinny-scrollbar'}
                  >
                    <RegistrationForm
                      signatureFieldId={signatureFieldId}
                      signatureFields={signmeRegisterFormStructure}
                      showFormErrors={showFormErrors}
                      currentRecipientData={currentRecipientData}
                    />
                  </div>
                </>

                <Block>
                  <Row>
                    <Col>
                      <Button onClick={() => handleClose()}>
                        {II18.get('title_cancel')}
                      </Button>
                    </Col>
                    <Col>
                      <Button onClick={applyChanges} raised fill>
                        {II18.get('title_next')}
                      </Button>
                    </Col>
                  </Row>
                </Block>
              </Block>
            )}
          </>
        )}
      </Page>
    </Popup>
  );
}

SignmeRegisterPopUp.propTypes = {
  noanimate: PropTypes.bool,
  opened: PropTypes.any,
  docId: PropTypes.string,
  signatureFieldId: PropTypes.string,
  onClose: PropTypes.func,
  onRegistrationCancelled: PropTypes.func,
  documentIsReadyToSign: PropTypes.bool,
  prepareDocumentToSignError: PropTypes.bool,
  prepareDocumentToSign: PropTypes.func,
  currentRecipientData: PropTypes.object,
  waitingForProcessing: PropTypes.bool,
  setWaitingForProcessing: PropTypes.func,
  templateMode: PropTypes.bool,
  signmeIdentityId: PropTypes.string,
  documentIsQualifiedSigned: PropTypes.bool,
  onSignFlowDone: PropTypes.func
};
